/**
 * Platform Mockup Renderer
 *
 * Central function to render platform-specific ad mockups
 */

import React from 'react';
import { FacebookAdMockup } from '../platform-specific/FacebookAdMockup';
import { GoogleSearchAdMockup } from '../platform-specific/GoogleSearchAdMockup';
import { GoogleDisplayAdMockup } from '../platform-specific/GoogleDisplayAdMockup';
import { GoogleShoppingAdMockup } from '../platform-specific/GoogleShoppingAdMockup';
import { InstagramAdMockup } from '../platform-specific/InstagramAdMockup';
import { YouTubeAdMockup } from '../platform-specific/YouTubeAdMockup';
import { TikTokAdMockup } from '../platform-specific/TikTokAdMockup';
import { PinterestAdMockup } from '../platform-specific/PinterestAdMockup';
import { TwitterAdMockup } from '../platform-specific/TwitterAdMockup';
import { CarouselAdMockup } from '../platform-specific/CarouselAdMockup';
import { GenericAdDisplay } from '../platform-specific/GenericAdDisplay';
import {
  mapToFacebookMockup,
  mapToGoogleSearchMockup,
  mapToGoogleDisplayMockup,
  mapToGoogleShoppingMockup,
  mapToInstagramMockup,
  mapToYouTubeMockup,
  mapToTikTokMockup,
  mapToPinterestMockup,
  mapToTwitterMockup,
  mapToCarouselMockup,
} from './platformMappers';
import { AdVariationData } from '../types';

interface RenderPlatformMockupProps {
  platform: string;
  ad: AdVariationData;
  conversationMedia: Array<{ filename: string; url: string; type: string }>;
}

/**
 * Renders the appropriate platform-specific mockup for an ad variation
 */
export const renderPlatformMockup = ({
  platform,
  ad,
  conversationMedia,
}: RenderPlatformMockupProps): React.ReactElement => {
  const normalizedPlatform = platform.toLowerCase();
  const format = ad.format?.toLowerCase();

  // Check for carousel format first (works across platforms)
  if (format === 'carousel') {
    return <CarouselAdMockup {...mapToCarouselMockup(ad, conversationMedia, platform)} />;
  }

  switch (normalizedPlatform) {
    case 'facebook':
    case 'meta':
      return <FacebookAdMockup {...mapToFacebookMockup(ad, conversationMedia)} />;

    case 'instagram':
      return <InstagramAdMockup {...mapToInstagramMockup(ad, conversationMedia)} />;

    case 'google_display':
      return <GoogleDisplayAdMockup {...mapToGoogleDisplayMockup(ad, conversationMedia)} />;

    case 'google_shopping':
      return <GoogleShoppingAdMockup {...mapToGoogleShoppingMockup(ad, conversationMedia)} />;

    case 'google_search':
    case 'google_ads':
    case 'google':
      return <GoogleSearchAdMockup {...mapToGoogleSearchMockup(ad)} />;

    case 'youtube':
      return <YouTubeAdMockup {...mapToYouTubeMockup(ad, conversationMedia)} />;

    case 'tiktok':
      return <TikTokAdMockup {...mapToTikTokMockup(ad, conversationMedia)} />;

    case 'pinterest':
      return <PinterestAdMockup {...mapToPinterestMockup(ad, conversationMedia)} />;

    case 'twitter':
    case 'x':
      return <TwitterAdMockup {...mapToTwitterMockup(ad, conversationMedia)} />;

    default:
      // Fallback to generic display for unsupported platforms
      return <GenericAdDisplay ad={ad} conversationMedia={conversationMedia} />;
  }
};
